import classNames from 'classnames';
import { includes } from 'lodash';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import NewMessage from 'components/ChatPractice/NewMessage';
import QuestionWithMetadata from 'components/Practice/QuestionWithMetadata';
import QuestionFieldContainer from 'components/TicketHandlingPractice/QuestionFieldContainer';
import { PracticeTypes } from 'constants/practiceTypes';
import { RecordingTypes } from 'constants/recordingTypes';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import AnswerKey from 'components/Questions/shared/AnswerKey/AnswerKey';
import {
  TabbedView,
  TabList,
  Tab,
  TabPanel,
} from 'components/shared/TabbedView';
import { useFeatureFlags } from 'components/shared/hooks';
import FeedbackCriteriaWrapper from './FeedbackCriteriaWrapper';
import ExampleRecording from '../media_recording/ExampleRecording';
import ProviderIndependentExampleRecording from '../media_recording/ProviderIndependentExampleRecording';
import AutomatedAnalysis from '../media_recording/AutomatedAnalysis';
import ElementPrompt from './ElementPrompt';

const TabbedElements = ({
  applyChanges,
  handleUpdate,
  content,
  options,
  handleCriteriaUpdate,
  handleCommentUpdate,
  handleQuestionMetadataUpdate,
  questionAutomatedAnalysis,
  addAutomatedAnalysisKeywordPath,
  canAddAutomatedAnalysis,
  getAutomatedAnalysisKeywordsPath,
  removeAutomatedAnalysisKeywordPath,
  browserSupport,
  currentUserId,
  apiKey,
  onError,
  props,
}) => {
  const {
    raw_choices: rawChoices,
    question_type: questionType,
    zendesk,
    zendesk_support_logo: zendeskSupportLogo,
    id,
    fields,
    question_associated_comments: questionAssociatedComments,
    question_metadata: questionMetadata,
  } = content;

  const [tabIndex, setTabIndex] = useState(0);

  const canUsePracticeFeature = useFeatureFlags('practice');
  const ziggeoForAllMedia = useFeatureFlags('ziggeo_for_all_media');

  const isPracticeQuestion = PracticeTypes.includes(questionType);
  const isRecordingQuestion = RecordingTypes.includes(questionType);
  const questionHasMetadata = includes(
    'email_reply ticket_handling',
    questionType,
  );
  const isReplyQuestion = includes(
    'email_reply chat ticket_handling',
    questionType,
  );

  const updateGradable = () => {
    const newOptions = { ...options, gradable: true };
    handleUpdate({ gradable: true });
    applyChanges({ options: newOptions });
  };

  return (
    <TabbedView onSelect={setTabIndex} selectedIndex={tabIndex}>
      <TabList className="pl-32 border-left-solid border-left-1 border-right-solid border-right-1 border-gray-light">
        <Tab>
          {isPracticeQuestion
            ? t('questions.answer_key.tabs.practice_details')
            : t('questions.answer_key.tabs.question_details')}
        </Tab>
        <Tab>{t('questions.answer_key.tabs.grading_resources')}</Tab>
      </TabList>
      <TabPanel>
        {' '}
        <>
          <ElementPrompt
            applyChanges={applyChanges}
            handleUpdate={handleUpdate}
            content={content}
          />
          <div
            className={classNames('EditorElement-body', {
              'EditorElement--replyQuestion': isReplyQuestion,
            })}
          >
            {questionHasMetadata && (
              <QuestionWithMetadata
                questionType={questionType}
                applyChanges={applyChanges}
                handleUpdate={handleUpdate}
                questionData={rawChoices ? JSON.parse(rawChoices) : {}}
                isZendesk={zendesk}
                zendeskSupportLogo={zendeskSupportLogo}
              />
            )}
            {questionType === 'chat' && (
              <NewMessage
                applyChanges={applyChanges}
                handleUpdate={handleUpdate}
                messageData={rawChoices ? JSON.parse(rawChoices) : ''}
              />
            )}
            {questionType === 'ticket_handling' && !zendesk && (
              <QuestionFieldContainer questionId={id} questionFields={fields} />
            )}
          </div>
          <div
            className={classNames('EditorElement-footer', {
              'EditorElement-footer recordingQuestionFooter':
                isRecordingQuestion,
            })}
          >
            {' '}
            {isRecordingQuestion && (
              <>
                <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                  {t('questions.element.highlighted_example')}
                </Text>
                {questionType !== 'audio_recording' ? (
                  <ProviderIndependentExampleRecording
                    browserSupport={browserSupport}
                    content={content}
                    currentUserId={currentUserId}
                    apiKey={apiKey}
                    provider={ziggeoForAllMedia ? 'ziggeo' : 'hippo_video'}
                    onError={onError}
                  />
                ) : (
                  <ExampleRecording
                    browserSupport={browserSupport}
                    content={content}
                    currentUserId={currentUserId}
                    onError={onError}
                  />
                )}
                {(questionAutomatedAnalysis || canAddAutomatedAnalysis) && (
                  <AutomatedAnalysis
                    getAutomatedAnalysisKeywordsPath={
                      getAutomatedAnalysisKeywordsPath
                    }
                    addAutomatedAnalysisKeywordPath={
                      addAutomatedAnalysisKeywordPath
                    }
                    removeAutomatedAnalysisKeywordPath={
                      removeAutomatedAnalysisKeywordPath
                    }
                    content={content}
                    applyChanges={applyChanges}
                    handleUpdate={handleUpdate}
                  />
                )}
              </>
            )}
          </div>
        </>
      </TabPanel>
      <TabPanel>
        <div
          className={classNames(
            'p-32 border-left-solid border-left-1 border-right-solid border-right-1 border-bottom-solid border-bottom-1 border-gray-light bdrs-small-bottom ',
            { 'pt-0': !isPracticeQuestion },
          )}
        >
          {canUsePracticeFeature && isPracticeQuestion && (
            <FeedbackCriteriaWrapper
              handleCriteriaUpdate={handleCriteriaUpdate}
              options={options}
              handleUpdate={handleUpdate}
              applyChanges={applyChanges}
              content={content}
            />
          )}
          <AnswerKey
            contentId={id}
            comments={questionAssociatedComments}
            questionMetadata={questionMetadata}
            handleCommentUpdate={handleCommentUpdate}
            handleQuestionMetadataUpdate={handleQuestionMetadataUpdate}
            updateGradable={updateGradable}
            applyChanges={applyChanges}
            handleUpdate={handleUpdate}
            isPracticeElement={canUsePracticeFeature && isPracticeQuestion}
          />
        </div>
      </TabPanel>
    </TabbedView>
  );
};

const mapStateToProps = state => ({
  apiKey: state.editor.ziggeoApiKey,
  browserSupport: state.meta.browserSupport,
  currentUserId: state.editor.currentUserId,
});

export default connect(mapStateToProps)(TabbedElements);
